/**
 * Calculator Modal
 * 
 * PURPOSE: Provide engineers with access to deterministic, verified calculations
 * via the C Calculation Engine. This modal allows direct interaction with the
 * same calculation tools available to the AI assistant.
 * 
 * CALCULATIONS:
 * - basic_math: Arithmetic, bitwise, number base conversions
 * - ohms_law: V=IR with solve-for logic
 * - power_triangle: 3-phase WYE power (kVA, kW, kVAR)
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select } from '../ui/select';

// Calculator types
type CalculationType = 'basic_math' | 'ohms_law' | 'power_triangle';

interface CalculatorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CalculationResult {
  success: boolean;
  calculation?: string;
  result?: Record<string, number | string>;
  formatted?: string;
  error?: string;
}

// API URL for the calculation engine
const CALC_ENGINE_URL = import.meta.env.VITE_CALC_ENGINE_URL || '';

export function CalculatorModal({ open, onOpenChange }: CalculatorModalProps) {
  const [selectedCalc, setSelectedCalc] = useState<CalculationType>('ohms_law');
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  
  // Ohm's Law inputs
  const [voltage, setVoltage] = useState('');
  const [current, setCurrent] = useState('');
  const [resistance, setResistance] = useState('');
  
  // Basic math inputs
  const [mathA, setMathA] = useState('');
  const [mathB, setMathB] = useState('');
  const [mathOp, setMathOp] = useState<string>('add');
  const [outputBase, setOutputBase] = useState<string>('decimal');
  
  // Power triangle inputs
  const [voltageLine, setVoltageLine] = useState('');
  const [currentLine, setCurrentLine] = useState('');
  const [powerFactor, setPowerFactor] = useState('0.8');

  const performCalculation = useCallback(async () => {
    setIsCalculating(true);
    setResult(null);
    
    try {
      let params: Record<string, unknown> = {};
      
      if (selectedCalc === 'ohms_law') {
        if (voltage) params.voltage = parseFloat(voltage);
        if (current) params.current = parseFloat(current);
        if (resistance) params.resistance = parseFloat(resistance);
      } else if (selectedCalc === 'basic_math') {
        params = {
          operation: mathOp,
          a: mathA,
          b: mathB,
        };
      } else if (selectedCalc === 'power_triangle') {
        params = {
          voltage_line: parseFloat(voltageLine),
          current_line: parseFloat(currentLine),
          power_factor: parseFloat(powerFactor),
        };
      }
      
      const response = await fetch(CALC_ENGINE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'calculate',
          calculation: selectedCalc,
          params,
          output_base: outputBase,
        }),
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setResult({
          success: true,
          calculation: selectedCalc,
          result: data.result,
          formatted: formatResult(selectedCalc, data.result),
        });
      } else {
        setResult({
          success: false,
          error: data.error || 'Calculation failed',
        });
      }
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      });
    } finally {
      setIsCalculating(false);
    }
  }, [selectedCalc, voltage, current, resistance, mathA, mathB, mathOp, outputBase, voltageLine, currentLine, powerFactor]);

  const formatResult = (calc: string, result: Record<string, number | string>): string => {
    if (!result) return 'No result';
    
    if (calc === 'ohms_law') {
      const parts: string[] = [];
      if (result.voltage !== undefined) parts.push(`Voltage: ${formatValue(result.voltage as number, 'V')}`);
      if (result.current !== undefined) parts.push(`Current: ${formatValue((result.current as number) * 1000, 'mA')}`);
      if (result.resistance !== undefined) parts.push(`Resistance: ${formatValue(result.resistance as number, 'Ω')}`);
      if (result.power !== undefined) parts.push(`Power: ${formatValue(result.power as number, 'W')}`);
      return parts.join('\n');
    }
    
    if (calc === 'basic_math') {
      if (result.formatted) return `Result: ${result.formatted}`;
      return `Result: ${result.value}`;
    }
    
    if (calc === 'power_triangle') {
      return [
        `Apparent Power: ${formatValue(result.apparent_power as number, 'kVA')}`,
        `Real Power: ${formatValue(result.real_power as number, 'kW')}`,
        `Reactive Power: ${formatValue(result.reactive_power as number, 'kVAR')}`,
        `Phase Angle: ${(result.phase_angle as number)?.toFixed(1)}°`,
      ].join('\n');
    }
    
    return JSON.stringify(result, null, 2);
  };

  const formatValue = (value: number, unit: string): string => {
    if (Math.abs(value) >= 1000) {
      return `${(value / 1000).toFixed(2)} k${unit}`;
    }
    return `${value.toFixed(2)} ${unit}`;
  };

  const clearInputs = () => {
    setVoltage('');
    setCurrent('');
    setResistance('');
    setMathA('');
    setMathB('');
    setVoltageLine('');
    setCurrentLine('');
    setPowerFactor('0.8');
    setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-xl">⚡</span>
            Engineering Calculator
            <Badge variant="info" className="ml-2">Verified</Badge>
          </DialogTitle>
          <DialogDescription>
            Deterministic calculations powered by the C Calculation Engine. 
            These results are unit-tested and safe for engineering decisions.
          </DialogDescription>
        </DialogHeader>

        {/* Calculator Type Selection */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={selectedCalc === 'ohms_law' ? 'default' : 'secondary'}
            onClick={() => { setSelectedCalc('ohms_law'); clearInputs(); }}
            size="sm"
          >
            Ohm's Law
          </Button>
          <Button
            variant={selectedCalc === 'basic_math' ? 'default' : 'secondary'}
            onClick={() => { setSelectedCalc('basic_math'); clearInputs(); }}
            size="sm"
          >
            Basic Math
          </Button>
          <Button
            variant={selectedCalc === 'power_triangle' ? 'default' : 'secondary'}
            onClick={() => { setSelectedCalc('power_triangle'); clearInputs(); }}
            size="sm"
          >
            Power Triangle
          </Button>
        </div>

        {/* Ohm's Law Calculator */}
        {selectedCalc === 'ohms_law' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enter any two values to calculate the third. V = I × R
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="voltage">Voltage (V)</Label>
                <Input
                  id="voltage"
                  type="number"
                  placeholder="28"
                  value={voltage}
                  onChange={(e) => setVoltage(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="current">Current (A)</Label>
                <Input
                  id="current"
                  type="number"
                  placeholder="0.02"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="resistance">Resistance (Ω)</Label>
                <Input
                  id="resistance"
                  type="number"
                  placeholder="1400"
                  value={resistance}
                  onChange={(e) => setResistance(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Basic Math Calculator */}
        {selectedCalc === 'basic_math' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Supports hex (0xFF), binary (0b1010), octal (0o17), and decimal input.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mathA">Value A</Label>
                <Input
                  id="mathA"
                  type="text"
                  placeholder="0xFF"
                  value={mathA}
                  onChange={(e) => setMathA(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mathOp">Operation</Label>
                <Select
                  id="mathOp"
                  value={mathOp}
                  onChange={(e) => setMathOp(e.target.value)}
                >
                  <option value="add">Add (+)</option>
                  <option value="subtract">Subtract (−)</option>
                  <option value="multiply">Multiply (×)</option>
                  <option value="divide">Divide (÷)</option>
                  <option value="and">AND (&amp;)</option>
                  <option value="or">OR (|)</option>
                  <option value="xor">XOR (^)</option>
                  <option value="shift_left">Shift Left (&lt;&lt;)</option>
                  <option value="shift_right">Shift Right (&gt;&gt;)</option>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mathB">Value B</Label>
                <Input
                  id="mathB"
                  type="text"
                  placeholder="0x10"
                  value={mathB}
                  onChange={(e) => setMathB(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="outputBase">Output Format</Label>
              <Select
                id="outputBase"
                className="max-w-xs"
                value={outputBase}
                onChange={(e) => setOutputBase(e.target.value)}
              >
                <option value="decimal">Decimal</option>
                <option value="hex">Hexadecimal (0x)</option>
                <option value="binary">Binary (0b)</option>
                <option value="octal">Octal (0o)</option>
              </Select>
            </div>
          </div>
        )}

        {/* Power Triangle Calculator */}
        {selectedCalc === 'power_triangle' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              3-Phase WYE Power Triangle calculation (typical for aircraft 400Hz motors)
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="voltageLine">Line Voltage (V)</Label>
                <Input
                  id="voltageLine"
                  type="number"
                  placeholder="115"
                  value={voltageLine}
                  onChange={(e) => setVoltageLine(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currentLine">Line Current (A)</Label>
                <Input
                  id="currentLine"
                  type="number"
                  placeholder="10"
                  value={currentLine}
                  onChange={(e) => setCurrentLine(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="powerFactor">Power Factor</Label>
                <Input
                  id="powerFactor"
                  type="number"
                  placeholder="0.8"
                  step="0.01"
                  min="0"
                  max="1"
                  value={powerFactor}
                  onChange={(e) => setPowerFactor(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Calculate Button */}
        <div className="flex justify-between items-center mt-4">
          <Button variant="secondary" onClick={clearInputs}>
            Clear
          </Button>
          <Button onClick={performCalculation} disabled={isCalculating}>
            {isCalculating ? 'Calculating...' : 'Calculate'}
          </Button>
        </div>

        {/* Results Display */}
        {result && (
          <div className={`mt-4 p-4 rounded-lg ${
            result.success 
              ? 'bg-success-light border border-success' 
              : 'bg-destructive-light border border-destructive'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={result.success ? 'text-success' : 'text-destructive'}>
                {result.success ? '✓' : '✗'}
              </span>
              <span className="font-semibold">
                {result.success ? 'Calculation Result' : 'Error'}
              </span>
            </div>
            <pre className="text-sm font-mono whitespace-pre-wrap">
              {result.success ? result.formatted : result.error}
            </pre>
            {result.success && result.result && (
              <details className="mt-2">
                <summary className="text-xs text-muted-foreground cursor-pointer">
                  Raw JSON
                </summary>
                <pre className="text-xs mt-1 p-2 bg-muted rounded">
                  {JSON.stringify(result.result, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}

        {/* Footer with info */}
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            💡 These calculations are also available via the AI assistant. 
            Ask questions like "What resistor do I need for a 28V LED at 20mA?"
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CalculatorModal;

